if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.headerBounds = { top: 0, bottom: 0 };
      this.currentScrollTop = 0;
      this.state = 'reset';
      this.ticking = false;
    }

    connectedCallback() {
      this.header = this.querySelector('theme-header');
      this.section = this.closest('.section-header-section');

      if (!this.header || !this.section) return;

      this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.onScrollHandler = this.onScroll.bind(this);
      this.onResizeHandler = this.updateBounds.bind(this);

      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      window.addEventListener('resize', this.onResizeHandler, { passive: true });

      this.createObserver();
      this.updateBounds();
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
      window.removeEventListener('resize', this.onResizeHandler);
    }

    createObserver() {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.updateBounds();
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    updateBounds() {
      const rect = this.header.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      this.headerBounds = {
        top: scrollTop + rect.top,
        bottom: scrollTop + rect.bottom
      };
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;

      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        let nextState = this.state;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          nextState = 'hide';
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          nextState = 'show';
        } else if (scrollTop <= this.headerBounds.top) {
          nextState = 'reset';
        }

        this.currentScrollTop = scrollTop;

        if (nextState !== this.state) {
          this.state = nextState;
          this.applyState(nextState);
        }

        this.ticking = false;
      });
    }

    applyState(state) {
      if (state === 'hide') {
        this.section.classList.add(
          'section-header-section--is-hidden',
          'section-header-section--is-sticky'
        );
        this.section.classList.remove('section-header-section--animate');
        this.header.classList.remove('is-sticky');
      }

      if (state === 'show') {
        this.section.classList.add(
          'section-header-section--is-sticky',
          'section-header-section--animate'
        );
        this.section.classList.remove('section-header-section--is-hidden');
        this.header.classList.add('is-sticky');
      }

      if (state === 'reset') {
        this.section.classList.remove(
          'section-header-section--is-hidden',
          'section-header-section--is-sticky',
          'section-header-section--animate'
        );
        this.header.classList.remove('is-sticky');
      }
    }
  }

  customElements.define('sticky-header', StickyHeader);
}

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.measureTicking = false;
      this.lastHeaderOffset = null;
      this.lastHeaderHeight = null;
      this.lastAnnouncementHeight = null;
    }

    connectedCallback() {
      this.header_section = document.querySelector('.section-header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.search_toggles = document.querySelectorAll('.thb-quick-search');
      this.search_drawer = document.getElementById('Search-Drawer');
      this.root = document.documentElement;
      this.body = document.body;

      if (!this.header_section || !this.toggle) return;

      this.boundOnScroll = this.onScrollMeasure.bind(this);
      this.boundOnResize = this.updateMeasurements.bind(this);
      this.boundOnKeyUp = this.onKeyUp.bind(this);

      document.addEventListener('keyup', this.boundOnKeyUp);
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnResize, { passive: true });
      window.addEventListener('orientationchange', this.boundOnResize, { passive: true });

      this.bindSearch();
      this.bindMobileToggle();
      this.bindMenuButtons();

      this.updateMeasurements();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.updateMeasurements());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.boundOnKeyUp);
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnResize);
      window.removeEventListener('orientationchange', this.boundOnResize);
    }

    onKeyUp(e) {
      if (!e.code || e.code.toUpperCase() !== 'ESCAPE' || !this.toggle) return;

      this.toggle.removeAttribute('open');
      this.toggle.classList.remove('active');

      this.querySelectorAll('details').forEach((detailsElement) => {
        this.closeSubmenu(detailsElement);
      });

      this.updateMeasurements();
    }

    bindSearch() {
      this.search_toggles.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();

          this.body.classList.add('open-cc', 'overflow-hidden');
          this.search_drawer.classList.toggle('active');

          setTimeout(() => {
            const input = this.search_drawer.querySelector('input[type="search"]');
            if (input) {
              input.focus({ preventScroll: true });
            }
          }, 100);

          dispatchCustomEvent('search:open');
          this.search_drawer.removeAttribute('inert');
        });
      });
    }

    bindMobileToggle() {
      const toggleButton = this.toggle.querySelector('.mobile-toggle');
      if (!toggleButton) return;

      toggleButton.addEventListener('click', (e) => {
        const toggleClass = this.toggle.classList;

        if (toggleClass.contains('active')) {
          e.preventDefault();
          this.body.classList.remove('overflow-hidden');
          toggleClass.remove('active');
          this.closeAnimation(this.toggle);

          setTimeout(() => {
            this.closeAll();
            this.updateMeasurements();
          }, 250);
        } else {
          this.body.classList.add('overflow-hidden');

          requestAnimationFrame(() => {
            toggleClass.add('active');
            this.updateMeasurements();
          });
        }

        window.dispatchEvent(new Event('resize-select'));
      });
    }

    bindMenuButtons() {
      if (!this.menu) return;

      this.menu
        .querySelectorAll('summary')
        .forEach((summary) =>
          summary.addEventListener('click', this.onSummaryClick.bind(this))
        );

      this.menu
        .querySelectorAll('.parent-link-back--button')
        .forEach((button) =>
          button.addEventListener('click', this.onCloseButtonClick.bind(this))
        );
    }

    updateMeasurements() {
      const announcementBar = document.querySelector('.header-announcement-bar');
      const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
      const headerHeight = this.header_section.offsetHeight;
      const headerOffset =
        window.innerWidth > 768
          ? Math.max(this.header_section.getBoundingClientRect().top, 0)
          : 0;

      if (announcementHeight !== this.lastAnnouncementHeight) {
        this.root.style.setProperty('--announcement-height', `${announcementHeight}px`);
        this.lastAnnouncementHeight = announcementHeight;
      }

      if (headerHeight !== this.lastHeaderHeight) {
        this.root.style.setProperty('--header-height', `${headerHeight}px`);
        this.lastHeaderHeight = headerHeight;
      }

      if (headerOffset !== this.lastHeaderOffset) {
        this.root.style.setProperty('--header-offset', `${headerOffset}px`);
        this.lastHeaderOffset = headerOffset;
      }
    }

    onScrollMeasure() {
      if (this.measureTicking) return;

      this.measureTicking = true;

      requestAnimationFrame(() => {
        const headerOffset =
          window.innerWidth > 768
            ? Math.max(this.header_section.getBoundingClientRect().top, 0)
            : 0;

        if (headerOffset !== this.lastHeaderOffset) {
          this.root.style.setProperty('--header-offset', `${headerOffset}px`);
          this.lastHeaderOffset = headerOffset;
        }

        this.measureTicking = false;
      });
    }

    closeAll() {
      this.querySelectorAll('details').forEach((detailsElement) => {
        this.closeSubmenu(detailsElement);
      });
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');

      if (this.querySelector('.parent-link-back--button') && this.menu) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        if (parentMenuElement) {
          parentMenuElement.classList.add('submenu-open');
        }
        this.updateMeasurements();
      }, 100);
    }

    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
      this.updateMeasurements();
    }

    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }

  customElements.define('theme-header', ThemeHeader);
}

if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = this.closest('.header');
      this.all_subs = this.querySelectorAll('.menu-item-has-children');
      this.submenus = this.querySelectorAll(
        '.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu'
      );

      if (!this.submenus.length) return;

      const _this = this;

      window.addEventListener(
        'resize',
        debounce(function () {
          _this.resizeSubMenus();
        }, 100),
        { passive: true }
      );

      _this.resizeSubMenus();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          _this.resizeSubMenus();
        });
      }

      this.all_subs.forEach((subs) => {
        subs.addEventListener('mouseenter', () => {
          this.header.classList.add('menu-item-hover');
        });

        subs.addEventListener('mouseleave', () => {
          this.header.classList.remove('menu-item-hover');
        });
      });
    }

    resizeSubMenus() {
      const bodyWidth = document.documentElement.clientWidth;

      this.submenus.forEach((submenu) => {
        const sub_submenus = submenu.querySelectorAll(
          ':scope > .menu-item-has-children > .sub-menu'
        );

        sub_submenus.forEach((sub_submenu) => {
          const parentList = sub_submenu.parentElement.parentElement;
          const parentRect = parentList.getBoundingClientRect();
          const total = sub_submenu.offsetWidth + parentRect.left + parentList.clientWidth + 10;

          if (total > bodyWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}